import { css } from '@emotion/react';
import { useQuery } from '@tanstack/react-query';
import {
  Link,
  useLocation,
  useRouteContext,
  useRouter,
} from '@tanstack/react-router';
import { App, Menu, type MenuProps } from 'antd';
import { useEffect } from 'react';
import { http } from '../features/common/http-client';
import { menuItems, type UserMenu } from '../features/common/menu';
import { canAccessRoutePath } from '../features/common/route-permission';

const MENU_OPEN_KEYS = [
  'analysis',
  'videos',
  'assets',
  'aigc_assets',
  'statistics',
  'system_management',
  'help_center',
  'images',
];

const getSelectedKey = (pathname: string) => {
  if (pathname.startsWith('/video/edit/')) {
    return '/video/history';
  }

  return pathname;
};

const toMenuItems = (
  items: UserMenu[],
  canAccessPath: (path: string) => boolean,
): MenuProps['items'] => {
  return items
    .map((item) => {
      if ('children' in item) {
        return {
          key: item.key,
          label: item.label,
          icon: item.icon,
          children: item.children
            .filter((child) => canAccessPath(child.path))
            .map((child) => ({
              key: child.path,
              label: <Link to={child.path}>{child.label}</Link>,
            })),
        };
      }

      if (item.path.startsWith('/') && !canAccessPath(item.path)) {
        return null;
      }

      return {
        key: item.path,
        label: item.external ? (
          <a href={item.path} target="_blank" rel="noopener noreferrer">
            {item.label}
          </a>
        ) : (
          <Link to={item.path}>{item.label}</Link>
        ),
        icon: item.icon,
      };
    })
    .filter((item) => {
      if (!item) {
        return false;
      }

      if ('children' in item) {
        return !!item.children?.length;
      }

      return true;
    });
};

let lastVersion = import.meta.env.NX_PUBLIC_APP_VERSION || 'development';

export const Sidebar = () => {
  const context = useRouteContext({ from: '/_creator-center' });
  const { modal } = App.useApp();
  const location = useLocation();
  const router = useRouter();

  const { data: latestVersion } = useQuery({
    queryKey: ['最新版本'],
    queryFn: async () => {
      try {
        const data = await http
          .get('bff-api/version', {
            prefix: '/',
          })
          .json<{ version: string }>();
        return data.version;
      } catch (error) {
        // 可能存在接口不通的情况，比如上层网关的原因这里
        // 这里即使拿不到也无所谓
        console.error(error);
        return null;
      }
    },
    refetchInterval: 60 * 1000,
  });

  useEffect(() => {
    if (!latestVersion) {
      return;
    }

    // 上次存储的远端版本与最新远端版本不一致时，弹出通知
    if (latestVersion !== lastVersion) {
      modal.info({
        title: '发现新版本',
        content: '页面已有更新，请刷新页面获取最新版本',
        okText: '刷新',
        onOk: () => {
          window.location.reload();
        },
      });
    }

    lastVersion = latestVersion;
  }, [latestVersion, modal]);

  const selected_key = getSelectedKey(location.pathname);

  const menu_items = toMenuItems(menuItems, (path) =>
    canAccessRoutePath(router, path, context.permissions),
  );

  return (
    <div className="flex h-full w-full min-w-0 flex-col border-r border-primary-200 bg-white">
      <div
        style={{ height: 'var(--ant-layout-header-height)' }}
        className="flex flex-none flex-col justify-center overflow-hidden whitespace-nowrap px-7"
      >
        <h1 className="text-base font-bold">AI视频创作中心</h1>
        <p className="mt-1 text-xs text-primary-400">打造你的爆款广告</p>
      </div>
      <div className="flex-auto overflow-y-auto">
        <Menu
          css={css`
            .ant-menu-item-selected {
              font-weight: bold;
            }
          `}
          classNames={{ root: 'bg-transparent !border-none' }}
          theme="light"
          mode="inline"
          items={menu_items}
          selectedKeys={[selected_key]}
          defaultOpenKeys={MENU_OPEN_KEYS}
        ></Menu>
      </div>

      <div className="border-t border-primary-200 p-4">
        <div className="text-center text-xs text-primary-500">
          <p>版本 1.0.0</p>
          <p className="mt-1">© 2026 AI视频创作中心</p>
        </div>
      </div>
    </div>
  );
};
